// HRMEZ · ROUTING — specialist, built conservatively from what the tool already stores.
// tools/HrmezTool.tsx writes every capture to `hrmez_entries` (status 'pending' while typed
// and unrouted, 'routed' once sent on, 'dismissed' when discarded; `classification` is set once
// a class is chosen). "Inputs to route" = this user's pending entries. HRMEZ has no notion of
// a blocked item, so none is shown; the routing state is just classified vs unclassified.
import { supabase } from '../../../../lib/supabase';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, useLoad } from './util';

const BASE = { id: 'hrmez', toolId: 'hrmez', orderFunction: 'regulate', title: 'ROUTING', deityName: 'HRMEZ', agentId: 'hrmez' } as const;

interface Pending {
  count: number;
  unclassified: number;
  oldest: string | null; // ISO created_at of the oldest pending entry
}

async function loadPending(userId: string): Promise<Pending> {
  const { data, error } = await supabase
    .from('hrmez_entries')
    .select('classification, created_at')
    .eq('user_id', userId)
    .eq('status', 'pending')
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw error;
  const rows = (data ?? []) as { classification: string | null; created_at: string | null }[];
  return {
    count: rows.length,
    unclassified: rows.filter((r) => !r.classification).length,
    oldest: rows.find((r) => r.created_at)?.created_at ?? null,
  };
}

export function useHrmezWidget(c: WidgetCtx): OrbWidgetModel {
  const { value } = useLoad<Pending>(c.userId ? () => loadPending(c.userId as string) : null, [c.userId, c.tick]);

  if (!value || value.count === 0) return emptyModel(BASE, c, 'Nothing to route');

  const classified = value.count - value.unclassified;
  const ageDays = value.oldest ? Math.max(0, Math.floor((c.now.getTime() - new Date(value.oldest).getTime()) / 86_400_000)) : null;
  const more = value.count >= 200 ? '+' : '';

  return {
    ...BASE,
    status: 'active',
    summary: `${value.count}${more} input${value.count === 1 ? '' : 's'} to route`,
    metrics: [
      { label: 'Unprocessed', value: `${value.count}${more}` },
      { label: 'Classified', value: classified },
      { label: 'Unclassified', value: value.unclassified },
    ],
    lines: [
      `Routing: ${classified} classified · ${value.unclassified} not yet classified`,
      ...(ageDays != null ? [`Oldest waiting: ${ageDays === 0 ? 'today' : `${ageDays} day${ageDays === 1 ? '' : 's'}`}`] : []),
    ],
    primaryAction: { label: 'Route next', run: () => c.openTool('hrmez'), tone: 'gold' },
    signals: { active: true },
  };
}
